import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { resolvePath, useLocation, useNavigate } from 'react-router-dom';
import ConfirmDialog from './modal/ConfirmDialog';
import { useActiveBusiness } from '../hooks/useActiveBusiness';
import './UnsavedChangesGuard.css';

const ConfirmLeaveContext = createContext(null);

const leaveNow = (proceed) => proceed();

/**
 * For a control that leaves the page without being a link (a button that calls
 * navigate): `confirmLeave(proceed, { to })` runs `proceed` straight away when
 * nothing is unsaved, and otherwise only once the user chooses to leave. Pass
 * `to` when you know the destination, so a move that keeps the page mounted
 * does not ask. Links need nothing: the guard catches their clicks itself.
 */
export const useConfirmLeave = () => useContext(ConfirmLeaveContext) || leaveNow;

/**
 * Asks before leaving a page that holds unsaved changes. Pages already report
 * those through registerUnsavedChanges (ActiveBusinessContext); this reads the
 * same registry when the user clicks a link, goes Back or Forward, or closes
 * or reloads the tab.
 *
 * BrowserRouter has no navigation blocking (useBlocker needs a data router), so
 * this works below the router: link clicks are caught on the way down, before
 * React Router's own handler runs, and Back/Forward is undone before the router
 * sees it. Navigation from code (a redirect after a save, a sign-out bounce) is
 * left alone, since the registry can lag a render behind a save.
 *
 * `isSamePage(fromPathname, toPathname)` says when two paths keep the same page
 * mounted, so moving between them loses nothing and should not ask.
 */
export const UnsavedChangesGuard = ({ children, isSamePage = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasUnsavedChanges } = useActiveBusiness();
  const [pendingLeave, setPendingLeave] = useState(null);
  const locationRef = useRef(location);
  // BrowserRouter numbers its history entries (history.state.idx); this is the
  // current one, to tell how far a Back or Forward moved.
  const historyIndexRef = useRef(null);
  // Set once the user has chosen to leave, so the tab-close prompt does not ask
  // again when the way out is a full page load.
  const leavingRef = useRef(false);
  const followingLinkRef = useRef(false);
  const revertingPopRef = useRef(false);
  const replayingPopRef = useRef(false);
  const triggerRef = useRef(null);

  useEffect(() => {
    locationRef.current = location;
    historyIndexRef.current = window.history.state?.idx ?? null;
    leavingRef.current = false;
  }, [location]);

  const isDirty = useCallback(
    () => typeof hasUnsavedChanges === 'function' && hasUnsavedChanges(),
    [hasUnsavedChanges],
  );

  const leavesPage = useCallback((nextPathname) => {
    const { pathname } = locationRef.current;
    if (nextPathname === pathname) return false;
    return !isSamePage?.(pathname, nextPathname);
  }, [isSamePage]);

  const ask = useCallback((proceed, trigger = null) => {
    triggerRef.current = trigger;
    setPendingLeave({ proceed });
  }, []);

  const confirmLeave = useCallback((proceed, { to = null } = {}) => {
    const nextPathname = to == null ? null : resolvePath(to, locationRef.current.pathname).pathname;
    if (!isDirty() || (nextPathname && !leavesPage(nextPathname))) {
      proceed();
      return;
    }
    ask(proceed);
  }, [ask, isDirty, leavesPage]);

  // Capture phase on the document runs before React's handlers on the root, so
  // stopping the click here keeps <Link> from navigating. Choosing to leave
  // clicks the same link again, so it does whatever it would have done (a
  // NavLink in the drawer closes the drawer, a `replace` link replaces).
  useEffect(() => {
    const followLink = (link, url) => {
      if (!link.isConnected) {
        navigate(`${url.pathname}${url.search}${url.hash}`);
        return;
      }
      followingLinkRef.current = true;
      try {
        link.click();
      } finally {
        followingLinkRef.current = false;
      }
    };

    const handleClick = (event) => {
      if (followingLinkRef.current || event.defaultPrevented || event.button !== 0) return;
      // A modified click opens a new tab or window and leaves this page as it is.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target?.closest?.('a[href]');
      if (!(link instanceof HTMLAnchorElement)) return;
      if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
      const url = new URL(link.href, window.location.href);
      // Leaving the app altogether is the browser's to ask about (beforeunload).
      if (url.origin !== window.location.origin) return;
      if (!isDirty() || !leavesPage(url.pathname)) return;
      event.preventDefault();
      event.stopPropagation();
      ask(() => followLink(link, url), link);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [ask, isDirty, leavesPage, navigate]);

  // By the time popstate fires the browser has already moved, so a Back or
  // Forward away from unsaved changes is stepped back before the router hears
  // of it, and replayed if the user chooses to leave. Capture puts this ahead of
  // the router's own popstate listener on window.
  useEffect(() => {
    const handlePopState = (event) => {
      if (replayingPopRef.current) {
        replayingPopRef.current = false;
        return;
      }
      if (revertingPopRef.current) {
        // The router never saw the move this undoes, so it must not see this one.
        revertingPopRef.current = false;
        event.stopImmediatePropagation();
        return;
      }
      const nextIndex = event.state?.idx;
      const currentIndex = historyIndexRef.current;
      // An entry the router did not make (a hash link, a page's own pushState)
      // has no index to step back by, so it goes through.
      if (!Number.isInteger(nextIndex) || !Number.isInteger(currentIndex)) return;
      const delta = nextIndex - currentIndex;
      if (!delta || !isDirty() || !leavesPage(window.location.pathname)) return;
      event.stopImmediatePropagation();
      revertingPopRef.current = true;
      window.history.go(-delta);
      ask(() => {
        replayingPopRef.current = true;
        window.history.go(delta);
      });
    };

    window.addEventListener('popstate', handlePopState, true);
    return () => window.removeEventListener('popstate', handlePopState, true);
  }, [ask, isDirty, leavesPage]);

  // Closing or reloading the tab, or following a link out of the app. Browsers
  // show their own wording here; a page cannot change it.
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (leavingRef.current || !isDirty()) return;
      event.preventDefault();
      // Older browsers only prompt when returnValue is set.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const stay = useCallback(() => setPendingLeave(null), []);

  const leave = () => {
    const proceed = pendingLeave?.proceed;
    setPendingLeave(null);
    leavingRef.current = true;
    proceed?.();
  };

  return (
    <ConfirmLeaveContext.Provider value={confirmLeave}>
      {children}
      <ConfirmDialog
        open={Boolean(pendingLeave)}
        className="unsaved-changes-dialog"
        title="Leave without saving?"
        description="You have unsaved changes on this page. If you leave now, they'll be lost."
        confirmLabel="Leave without saving"
        cancelLabel="Keep editing"
        confirmClassName="pro-btn pro-btn--danger"
        cancelClassName="pro-btn"
        onConfirm={leave}
        onCancel={stay}
        triggerRef={triggerRef}
      />
    </ConfirmLeaveContext.Provider>
  );
};

export default UnsavedChangesGuard;
